import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { 
  TrendingUp, 
  Activity,
  Thermometer,
  Droplets,
  Calendar,
  Download
} from 'lucide-react';

const Analytics = ({ sensors }) => {
  // Générer des données d'analyse
  const generateAnalyticsData = () => {
    const hourlyData = [];
    const now = new Date();
    
    for (let i = 23; i >= 0; i--) {
      const hour = (now.getHours() - i + 24) % 24;
      hourlyData.push({
        hour: `${hour}h`,
        avgHumidity: Math.random() * 20 + 40,
        avgTemperature: Math.random() * 10 + 18,
        activeSensors: Math.floor(Math.random() * 3) + 3
      });
    }
    
    return hourlyData;
  };

  const analyticsData = generateAnalyticsData();

  // Données par capteur
  const sensorStats = sensors.map(sensor => ({
    name: sensor.name.replace('Capteur ', ''),
    humidity: sensor.humidity,
    temperature: sensor.temperature,
    battery: sensor.battery,
    status: sensor.status
  }));

  // Distribution des statuts
  const statusData = [
    { 
      name: 'Actifs', 
      value: sensors.filter(s => s.status === 'active').length,
      color: '#22C55E'
    },
    { 
      name: 'Alertes', 
      value: sensors.filter(s => s.status === 'warning').length,
      color: '#F59E0B'
    },
    { 
      name: 'Hors ligne', 
      value: sensors.filter(s => s.status === 'offline').length,
      color: '#EF4444'
    }
  ];

  // Statistiques générales
  const totalSensors = sensors.length;
  const avgHumidity = sensors.reduce((acc, s) => acc + s.humidity, 0) / totalSensors;
  const avgTemperature = sensors.reduce((acc, s) => acc + s.temperature, 0) / totalSensors;
  const avgBattery = sensors.reduce((acc, s) => acc + s.battery, 0) / totalSensors;

  const stats = [
    {
      name: 'Humidité moyenne',
      value: `${avgHumidity.toFixed(1)}%`,
      change: '+2.3%',
      changeType: 'increase',
      icon: Droplets,
      color: 'blue'
    },
    {
      name: 'Température moyenne',
      value: `${avgTemperature.toFixed(1)}°C`,
      change: '+0.8°C',
      changeType: 'increase',
      icon: Thermometer,
      color: 'orange'
    },
    {
      name: 'Batterie moyenne',
      value: `${avgBattery.toFixed(0)}%`,
      change: '-3.2%',
      changeType: 'decrease',
      icon: Activity,
      color: 'green'
    },
    {
      name: 'Temps de fonctionnement',
      value: '99.2%',
      change: '+0.1%',
      changeType: 'increase',
      icon: TrendingUp,
      color: 'purple'
    }
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-fade-in">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient mb-2">
            Analytiques
          </h1>
          <p className="text-slate-600">
            Analyse détaillée des performances de vos capteurs
          </p>
        </div>
        
        <button className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors duration-200">
          <Download className="h-4 w-4" />
          <span>Exporter</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={stat.name}
              className="glass-effect rounded-2xl p-6 card-hover animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-r from-${stat.color}-50 to-${stat.color}-100 rounded-xl`}>
                  <Icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
                <span className={`text-sm font-medium ${
                  stat.changeType === 'increase' ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {stat.change}
                </span>
              </div>
              
              <div>
                <p className="text-3xl font-bold text-slate-800 mb-1">
                  {stat.value}
                </p>
                <p className="text-sm font-medium text-slate-600">
                  {stat.name}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Trends Chart */}
        <div className="xl:col-span-2 glass-effect rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-slate-800">
              Évolution sur 24h
            </h3>
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="text-slate-600">Humidité</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-slate-600">Température</span>
              </div>
            </div>
          </div>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analyticsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis 
                  dataKey="hour" 
                  stroke="#64748B"
                  fontSize={12}
                />
                <YAxis 
                  stroke="#64748B"
                  fontSize={12}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="avgHumidity"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  name="Humidité (%)"
                />
                <Line
                  type="monotone"
                  dataKey="avgTemperature"
                  stroke="#F97316"
                  strokeWidth={2}
                  dot={{ fill: '#F97316', strokeWidth: 2, r: 4 }}
                  name="Température (°C)"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Status Distribution */}
        <div className="glass-effect rounded-2xl p-6">
          <h3 className="text-xl font-semibold text-slate-800 mb-6">
            État des capteurs
          </h3>
          
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="space-y-3 mt-4">
            {statusData.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm font-medium text-slate-700">
                    {item.name}
                  </span>
                </div>
                <span className="text-sm font-bold text-slate-800">
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sensor Comparison */}
      <div className="glass-effect rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-slate-800">
            Comparaison des capteurs
          </h3>
          <div className="flex items-center space-x-2 text-sm text-slate-500">
            <Calendar className="h-4 w-4" />
            <span>Dernières valeurs</span>
          </div>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={sensorStats} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="name" 
                stroke="#64748B"
                fontSize={12}
              />
              <YAxis 
                stroke="#64748B"
                fontSize={12}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Bar dataKey="humidity" fill="#3B82F6" name="Humidité (%)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="temperature" fill="#F97316" name="Température (°C)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Performance Table */}
      <div className="glass-effect rounded-2xl p-6">
        <h3 className="text-xl font-semibold text-slate-800 mb-6">
          Résumé des performances
        </h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 font-medium text-slate-700">Capteur</th>
                <th className="text-left py-3 px-4 font-medium text-slate-700">Emplacement</th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">Humidité</th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">Température</th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">Batterie</th>
                <th className="text-center py-3 px-4 font-medium text-slate-700">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sensors.map((sensor) => (
                <tr key={sensor.id} className="hover:bg-slate-50 transition-colors duration-200">
                  <td className="py-4 px-4 font-medium text-slate-800">
                    {sensor.name}
                  </td>
                  <td className="py-4 px-4 text-slate-600">
                    {sensor.location}
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-semibold text-slate-800">
                      {sensor.humidity.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className="font-semibold text-slate-800">
                      {sensor.temperature.toFixed(1)}°C
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-12 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${
                            sensor.battery > 50 ? 'bg-green-500' :
                            sensor.battery > 20 ? 'bg-yellow-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${sensor.battery}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-slate-700">
                        {sensor.battery}%
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`status-indicator ${
                      sensor.status === 'active' ? 'status-active' :
                      sensor.status === 'warning' ? 'status-warning' :
                      'status-offline'
                    }`}>
                      {sensor.status === 'active' ? 'Actif' :
                       sensor.status === 'warning' ? 'Alerte' :
                       'Hors ligne'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Analytics;